import {
    TransactionWithIdentifier,
    TransactionWithIdentifiersAndSetup,
    OrderedTransactions,
    BundleOptions,
    combineTransactionPromises,
    TransactionCombineOptions,
    isVersionedTransaction
} from "@bridgesplit/react";
import {
    SendableTransaction,
    SendableTransactionsWithSetup,
    Result,
    TransactionsWithDataGenerateReturn,
    TransactionSettingsHeader,
    AnalyticsInfo
} from "@bridgesplit/utils";
import { Commitment } from "@solana/web3.js";
import { useSelector } from "react-redux";

import { AbfGeneratorResult, GenerateTransactionParams, TransactionSimulationResult } from "../types";
import { getTransactionHeadersFromCookies } from "../utils";
import { getSlice } from "../reducers/authSlice";

// When true, any batch containing a Switchboard oracle crank (detected by the secp256k1
// precompile program, whose offsets are corrupted by priority-fee-injecting wallets) is
// broadcast via normal RPC priority path instead of Jito bundle.
const AVOID_JITO_FOR_SWITCHBOARD_CRANK = true;

// Secp256k1 precompile program — present in Switchboard on-demand crank txs, with hardcoded
// instruction_index offsets that require the crank tx's ix ordering to remain untouched.
const SECP256K1_PROGRAM_ID = "KeccakSecp256k11111111111111111111111111111";

function txContainsSwitchboardCrank(tx: SendableTransaction): boolean {
    if (isVersionedTransaction(tx)) {
        return tx.message.compiledInstructions.some(
            (ix) => tx.message.staticAccountKeys[ix.programIdIndex]?.toBase58() === SECP256K1_PROGRAM_ID
        );
    }
    return tx.instructions.some((ix) => ix.programId.toBase58() === SECP256K1_PROGRAM_ID);
}

function shouldUseJitoBundle(transactions: TransactionWithIdentifier[]): boolean {
    if (transactions.length <= 1) return false;
    if (
        AVOID_JITO_FOR_SWITCHBOARD_CRANK &&
        transactions.some(({ transaction }) => txContainsSwitchboardCrank(transaction))
    ) {
        return false;
    }
    return true;
}

export function buildBundledTransactionBatch(
    transactions: TransactionWithIdentifier[],
    commitmentLevel?: Commitment
): OrderedTransactions {
    if (!transactions.length) return [];

    return [
        {
            transactions,
            commitmentLevel,
            bundle: shouldUseJitoBundle(transactions) ? BundleOptions.BundleOnly : undefined
        }
    ];
}

export function bundleMultiTransactionBatches(transactions: OrderedTransactions): OrderedTransactions {
    return transactions.map((batch) => {
        if (batch.bundle !== undefined) return batch;
        return {
            ...batch,
            bundle: shouldUseJitoBundle(batch.transactions) ? BundleOptions.BundleOnly : undefined
        };
    });
}

export function useAbfGenerateTransaction() {
    const state = useSelector(getSlice);

    return async function abfGenerateTransaction<ParamsType extends object, QueryType extends object>(
        props: GenerateTransactionParams<ParamsType, SendableTransaction[], QueryType>
    ): Promise<Result<TransactionWithIdentifier[]>> {
        const { params, identifier, firstTxIdentifier, generateFunction, queryParams, analyticsInfo, txnSettingsOverride } = props;
        try {
            const auth = getTransactionHeadersFromCookies({ authSlice: state });
            if (!auth.isOk()) return Result.err(auth);
            if (txnSettingsOverride) {
                auth.unwrap().txnSettings = {
                    ...auth.unwrap().txnSettings,
                    ...txnSettingsOverride
                } as TransactionSettingsHeader;
            }
            const txns = await generateFunction(auth.unwrap(), params, queryParams, analyticsInfo);
            props.onGenerate?.(txns);
            const txnWithIdentifiers = txns.map((transaction, i): TransactionWithIdentifier => {
                return {
                    transaction,
                    identifier: resolveTxIdentifier(i, txns.length, identifier, firstTxIdentifier),
                    analyticsInfo
                };
            });
            return Result.ok(txnWithIdentifiers);
        } catch (error) {
            return Result.err(error);
        }
    };
}

export function useAbfGenerateTransactionWithData() {
    const state = useSelector(getSlice);

    return async function abfGenerateTransaction<
        ParamsType extends object,
        DataType extends object,
        TransactionType extends TransactionsWithDataGenerateReturn<DataType>,
        QueryType extends object
    >(
        props: GenerateTransactionParams<ParamsType, TransactionType, QueryType>
    ): Promise<Result<TransactionWithIdentifier[]>> {
        const { params, identifier, firstTxIdentifier, generateFunction, queryParams, analyticsInfo, txnSettingsOverride } = props;
        try {
            const auth = getTransactionHeadersFromCookies({ authSlice: state });
            if (!auth.isOk()) return Result.err(auth);
            if (txnSettingsOverride) {
                auth.unwrap().txnSettings = {
                    ...auth.unwrap().txnSettings,
                    ...txnSettingsOverride
                } as TransactionSettingsHeader;
            }

            const txns = await generateFunction(auth.unwrap(), params, queryParams, analyticsInfo);
            props.onGenerate?.(txns);

            const txnWithIdentifiers = txns.transactions.map((transaction, i): TransactionWithIdentifier => {
                return {
                    transaction,
                    identifier: resolveTxIdentifier(i, txns.transactions.length, identifier, firstTxIdentifier),
                    analyticsInfo
                };
            });
            return Result.ok(txnWithIdentifiers);
        } catch (error) {
            return Result.err(error);
        }
    };
}

function resolveTxIdentifier(
    index: number,
    total: number,
    identifier: string | undefined,
    firstTxIdentifier: string | undefined
): string {
    if (index === 0 && total > 1 && firstTxIdentifier) return firstTxIdentifier;
    return identifier ?? "" + index;
}

export async function wrapAsAbfGeneratorResult(
    txnPromise: Promise<Result<TransactionWithIdentifier[]>>,
    createSimulationPromise: (
        txns: OrderedTransactions,
        analyticsInfo?: AnalyticsInfo
    ) => Promise<TransactionSimulationResult | undefined>,
    options: TransactionCombineOptions,
    analyticsInfo?: AnalyticsInfo
): AbfGeneratorResult {
    try {
        const combined = await combineTransactionPromises([txnPromise], options);
        if (combined.isErr()) {
            // Pass the failed Result through directly — unwrap() on an err returns undefined
            // and would replace the underlying API error message with UNKNOWN_ERROR.
            return Result.err(combined);
        }
        const transactions = combined.unwrap();
        return Result.ok({ transactions, simulationPromise: createSimulationPromise(transactions, analyticsInfo) });
    } catch (error) {
        return Result.err(error);
    }
}

export async function wrapBundledAsAbfGeneratorResult(
    txnPromise: Promise<Result<TransactionWithIdentifier[]>>,
    createSimulationPromise: (
        txns: OrderedTransactions,
        analyticsInfo?: AnalyticsInfo
    ) => Promise<TransactionSimulationResult | undefined>,
    commitmentLevel?: Commitment,
    analyticsInfo?: AnalyticsInfo
): AbfGeneratorResult {
    try {
        const txns = await txnPromise;
        if (txns.isErr()) {
            return Result.err(txns);
        }
        const transactions = buildBundledTransactionBatch(txns.unwrap(), commitmentLevel);
        return Result.ok({ transactions, simulationPromise: createSimulationPromise(transactions, analyticsInfo) });
    } catch (error) {
        return Result.err(error);
    }
}

export function useAbfGenerateTransactionWithSetup() {
    const state = useSelector(getSlice);
    return async function abfGenerateTransaction<ParamsType extends object, QueryType extends object>(
        props: GenerateTransactionParams<ParamsType, SendableTransactionsWithSetup, QueryType>
    ): Promise<Result<TransactionWithIdentifiersAndSetup>> {
        const { params, identifier, queryParams, analyticsInfo, generateFunction, txnSettingsOverride } = props;
        try {
            const auth = getTransactionHeadersFromCookies({ authSlice: state });
            if (!auth.isOk()) return Result.err(auth);
            if (txnSettingsOverride) {
                auth.unwrap().txnSettings = {
                    ...auth.unwrap().txnSettings,
                    ...txnSettingsOverride
                } as TransactionSettingsHeader;
            }
            const result = await generateFunction(auth.unwrap(), params, queryParams, analyticsInfo);
            props.onGenerate?.(result);
            const { setupTransactions, transactions } = result;
            const setUpTransactionsWithIdentifiers = setupTransactions.map(
                (transaction, i): TransactionWithIdentifier => {
                    return {
                        transaction,
                        identifier: "Set up " + (identifier ?? "") + i,
                        analyticsInfo
                    };
                }
            );

            const transactionsWithIdentifiers = transactions.map((transaction, i): TransactionWithIdentifier => {
                return {
                    transaction,
                    identifier: identifier ?? "" + i,
                    analyticsInfo
                };
            });
            return Result.ok({
                setupTransactions: setUpTransactionsWithIdentifiers,
                transactions: transactionsWithIdentifiers
            });
        } catch (error) {
            return Result.err(error);
        }
    };
}
